import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type FeedbackCategory = "Bug" | "Idea" | "Question" | "Other";
export type FeedbackStatus = "New" | "In Progress" | "Resolved" | "Declined";

export const CATEGORIES: Array<{ key: FeedbackCategory; hint: string }> = [
  { key: "Bug", hint: "something is broken or wrong" },
  { key: "Idea", hint: "a suggestion or improvement" },
  { key: "Question", hint: "not sure how something works" },
  { key: "Other", hint: "anything else" },
];

export const STATUSES: FeedbackStatus[] = ["New", "In Progress", "Resolved", "Declined"];

export interface FeedbackItem {
  id: string;
  userId: string;
  category: FeedbackCategory;
  body: string;
  page: string | null;
  status: FeedbackStatus;
  adminRemark: string | null;
  raiserUnseen: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackAdminItem extends Omit<FeedbackItem, "raiserUnseen"> {
  raiserName: string | null;
  raiserEmail: string;
  notifiedAt: string | null;
}

export function useMyFeedback() {
  return useQuery({
    queryKey: ["feedback", "mine"],
    queryFn: () => api<{ feedback: FeedbackItem[]; unseen: number }>("/feedback/mine"),
    staleTime: 30_000,
  });
}

export function useFeedbackActions() {
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: (body: { category: FeedbackCategory; body: string; page: string }) =>
      api<{ ok: true; feedback: FeedbackItem; notified: boolean }>("/feedback", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["feedback"] }),
  });
  const seen = useMutation({
    mutationFn: (id: string) => api(`/feedback/${id}/seen`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["feedback", "mine"] }),
  });
  return { submit, seen };
}

export function useAllFeedback(filter: { status: string; category: string }) {
  const params = new URLSearchParams();
  if (filter.status) params.set("status", filter.status);
  if (filter.category) params.set("category", filter.category);
  return useQuery({
    queryKey: ["feedback", "all", filter.status, filter.category],
    queryFn: () => api<{ feedback: FeedbackAdminItem[]; newCount: number }>(`/feedback?${params}`),
  });
}

export function useManageFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; status?: FeedbackStatus; adminRemark?: string | null }) =>
      api(`/feedback/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["feedback"] }),
  });
}
